import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { gsap } from "gsap";
import { motionSafe } from "../hooks";
import type { Item } from "../types";
import { cn } from "../lib";

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const layerColor = (
  faceColor: string,
  depthColor: string,
  index: number,
  total: number,
) => {
  const progress = total <= 1 ? 1 : index / total;
  const faceMix = Math.round((1 - progress * progress) * 72 + 4);
  return `color-mix(in srgb, ${faceColor} ${faceMix}%, ${depthColor})`;
};

export function DepthText({
  text,
  layers = 34,
  depth = 2.4,
  faceColor = "#f8fafc",
  depthColor = "#7c3aed",
  tilt = 7.5,
  pointerTracking = true,
  smoothing = 0.14,
  perspective = 900,
  autoOrbit = true,
  orbitSpeed = 0.35,
  fontSize = 104,
  fontWeight = 900,
  shadow = true,
}: {
  text: string;
  layers?: number;
  depth?: number;
  faceColor?: string;
  depthColor?: string;
  tilt?: number;
  pointerTracking?: boolean;
  smoothing?: number;
  perspective?: number;
  autoOrbit?: boolean;
  orbitSpeed?: number;
  fontSize?: number;
  fontWeight?: number;
  shadow?: boolean;
}) {
  const rootRef = useRef<HTMLHeadingElement>(null);
  const stageRef = useRef<HTMLSpanElement>(null);
  const safeLayers = clamp(Math.round(layers || 1), 2, 64);
  const safeDepth = clamp(depth || 0, 0, 12);
  const safeTilt = clamp(tilt || 0, 0, 12);
  const safeSmoothing = clamp(smoothing || 0.14, 0.02, 0.35);
  const safePerspective = clamp(perspective || 900, 300, 2000);
  const safeOrbitSpeed = clamp(orbitSpeed || 0, 0, 2);
  const baseRotation = useMemo(
    () => ({ x: -safeTilt * 0.32, y: safeTilt * 0.42 }),
    [safeTilt],
  );
  const depthLayers = useMemo(
    () =>
      Array.from({ length: safeLayers }, (_, layerIndex) => {
        const index = safeLayers - layerIndex;
        return {
          index,
          color: layerColor(faceColor, depthColor, index, safeLayers),
          transform: `translateZ(${-index * safeDepth}px)`,
        };
      }),
    [safeLayers, safeDepth, faceColor, depthColor],
  );

  useEffect(() => {
    const root = rootRef.current;
    const stage = stageRef.current;
    if (!root || !stage) return;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const finePointer = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;
    const canTrack = pointerTracking && finePointer && !reduced;
    const current = { ...baseRotation };
    const target = { ...baseRotation };
    let frame = 0;
    let activePointer = false;
    const startTime = performance.now();
    const apply = () => {
      stage.style.transform = `rotateX(${current.x.toFixed(3)}deg) rotateY(${current.y.toFixed(3)}deg)`;
    };
    if (reduced) {
      apply();
      return;
    }
    const pointerMove = (event: PointerEvent) => {
      const rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      activePointer = true;
      const x = clamp(
        (event.clientX - (rect.left + rect.width / 2)) / (rect.width * 0.8),
        -1,
        1,
      );
      const y = clamp(
        (event.clientY - (rect.top + rect.height / 2)) / (rect.height * 0.8),
        -1,
        1,
      );
      target.x = baseRotation.x - y * safeTilt;
      target.y = baseRotation.y + x * safeTilt;
    };
    const pointerLeave = () => {
      activePointer = false;
      target.x = baseRotation.x;
      target.y = baseRotation.y;
    };
    if (canTrack) {
      window.addEventListener("pointermove", pointerMove);
      window.addEventListener("pointerleave", pointerLeave);
      window.addEventListener("blur", pointerLeave);
    }
    const tick = (now: number) => {
      if ((!canTrack || !activePointer) && autoOrbit) {
        const orbit = ((now - startTime) / 1000) * safeOrbitSpeed * Math.PI * 2;
        const amount = canTrack ? 0.16 : 0.48;
        target.x = baseRotation.x + Math.sin(orbit) * safeTilt * amount;
        target.y = baseRotation.y + Math.cos(orbit * 0.85) * safeTilt * amount;
      }
      current.x += (target.x - current.x) * safeSmoothing;
      current.y += (target.y - current.y) * safeSmoothing;
      apply();
      frame = requestAnimationFrame(tick);
    };
    apply();
    frame = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("pointermove", pointerMove);
      window.removeEventListener("pointerleave", pointerLeave);
      window.removeEventListener("blur", pointerLeave);
      cancelAnimationFrame(frame);
    };
  }, [
    autoOrbit,
    baseRotation,
    pointerTracking,
    safeOrbitSpeed,
    safeSmoothing,
    safeTilt,
  ]);

  const style = {
    "--rb-depth-perspective": `${safePerspective}px`,
    "--rb-depth-size": `${fontSize}px`,
    "--rb-depth-weight": fontWeight,
    "--rb-depth-face": faceColor,
    "--rb-depth-shadow": shadow
      ? `0 22px 34px color-mix(in srgb, ${depthColor} 36%, transparent), 0 4px 8px rgb(0 0 0 / .28)`
      : "none",
  } as CSSProperties;
  return (
    <h1 ref={rootRef} className="rb-depth-text" style={style} aria-label={text}>
      <span ref={stageRef} className="rb-depth-text__stage" aria-hidden="true">
        {depthLayers.map((layer) => (
          <span
            className="rb-depth-text__layer"
            key={layer.index}
            style={{ color: layer.color, transform: layer.transform }}
          >
            {text}
          </span>
        ))}
        <span className="rb-depth-text__face">{text}</span>
      </span>
    </h1>
  );
}

const MAX_PIXELS = 220;
const KEYFRAME_STEPS = 14;
type PixelPattern =
  | "random"
  | "center"
  | "edges"
  | "left-to-right"
  | "right-to-left"
  | "top-to-bottom"
  | "bottom-to-top"
  | "diagonal"
  | "spiral";
const patterns: Record<PixelPattern, (x: number, y: number) => number | null> =
  {
    random: () => null,
    center: (x, y) => Math.hypot(x - 0.5, y - 0.5) / Math.SQRT1_2,
    edges: (x, y) => Math.min(x, 1 - x, y, 1 - y) * 2,
    "left-to-right": (x) => x,
    "right-to-left": (x) => 1 - x,
    "top-to-bottom": (_x, y) => y,
    "bottom-to-top": (_x, y) => 1 - y,
    diagonal: (x, y) => (x + y) / 2,
    spiral: (x, y) => {
      const angle = (Math.atan2(y - 0.5, x - 0.5) + Math.PI) / (Math.PI * 2);
      const radius = Math.hypot(x - 0.5, y - 0.5) / Math.SQRT1_2;
      return (angle + radius) % 1;
    },
  };
const easingMap: Record<string, [number, number, number, number]> = {
  linear: [0, 0, 1, 1],
  ease: [0.25, 0.1, 0.25, 1],
  "ease-in": [0.42, 0, 1, 1],
  "ease-out": [0, 0, 0.58, 1],
  "ease-in-out": [0.42, 0, 0.58, 1],
};
const noise = (seed: number) => {
  const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return value - Math.floor(value);
};
const makeEasing = (value: string) => {
  const match = /cubic-bezier\(([^)]+)\)/.exec(value);
  const points = match ? match[1].split(",").map(Number) : easingMap[value];
  if (!points || points.length !== 4 || points.some(Number.isNaN))
    return makeEasing("ease");
  const [x1, y1, x2, y2] = points;
  if (x1 === y1 && x2 === y2) return (progress: number) => progress;
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  return (progress: number) => {
    let t = progress;
    for (let i = 0; i < 5; i += 1) {
      const slope = (3 * ax * t + 2 * bx) * t + cx;
      if (!slope) break;
      t -= (((ax * t + bx) * t + cx) * t - progress) / slope;
    }
    t = clamp(t, 0, 1);
    return ((ay * t + by) * t + cy) * t;
  };
};
const coverScale = (size: number, gap: number, radius: number) => {
  const p = clamp(radius, 0, 50) / 100;
  const corner = Math.SQRT1_2 / (Math.SQRT2 * (0.5 - p) + p);
  return ((size + gap) / size) * Math.max(1, corner);
};
type Grid = ReturnType<typeof buildGrid>;
function buildGrid({
  width,
  height,
  pixelSize,
  gap,
  pattern,
  randomness,
}: {
  width: number;
  height: number;
  pixelSize: number;
  gap: number;
  pattern: PixelPattern;
  randomness: number;
}) {
  let size = pixelSize;
  let columns = Math.max(1, Math.ceil((width + gap) / (size + gap)));
  let rows = Math.max(1, Math.ceil((height + gap) / (size + gap)));
  if (columns * rows > MAX_PIXELS) {
    size = Math.ceil(size * Math.sqrt((columns * rows) / MAX_PIXELS));
    columns = Math.max(1, Math.ceil((width + gap) / (size + gap)));
    rows = Math.max(1, Math.ceil((height + gap) / (size + gap)));
  }
  const stride = size + gap;
  const originX = (width - (columns * stride - gap)) / 2;
  const originY = (height - (rows * stride - gap)) / 2;
  const order = patterns[pattern] ?? patterns.random;
  const mix = clamp(randomness, 0, 1);
  const pixels = [];
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const index = row * columns + column;
      const x = columns <= 1 ? 0.5 : column / (columns - 1);
      const y = rows <= 1 ? 0.5 : row / (rows - 1);
      const base = order(x, y);
      const random = noise(index + 1);
      pixels.push({
        id: index,
        left: originX + column * stride,
        top: originY + row * stride,
        offset: base === null ? random : base * (1 - mix) + random * mix,
      });
    }
  }
  return { pixels, size, gap, width, height };
}
function buildKeyframes({
  ease,
  startScale,
  endScale,
  spin,
  fade,
}: {
  ease: (value: number) => number;
  startScale: number;
  endScale: number;
  spin: number;
  fade: boolean;
}) {
  const windowFrames: Keyframe[] = [];
  const contentFrames: Keyframe[] = [];
  for (let step = 0; step <= KEYFRAME_STEPS; step += 1) {
    const progress = step / KEYFRAME_STEPS;
    const eased = ease(progress);
    const scale = startScale + (endScale - startScale) * eased;
    const angle = spin * (1 - eased);
    windowFrames.push({
      offset: progress,
      opacity: fade ? Math.min(1, eased * 1.6) : 1,
      transform: `rotate(${angle}deg) scale(${scale})`,
    });
    contentFrames.push({
      offset: progress,
      transform: `scale(${1 / scale}) rotate(${-angle}deg)`,
    });
  }
  return { windowFrames, contentFrames };
}

export function PixelSwap({
  firstContent,
  secondContent,
  pixelSize = 64,
  gap = 0,
  pixelRadius = 0,
  pixelSpin = 0,
  pixelScale = 0.35,
  fade = true,
  duration = 1400,
  pixelDuration = 450,
  pattern = "random",
  randomness = 0,
  easing = "cubic-bezier(0.22, 1, 0.36, 1)",
  trigger = "hover",
}: {
  firstContent: ReactNode;
  secondContent: ReactNode;
  pixelSize?: number;
  gap?: number;
  pixelRadius?: number;
  pixelSpin?: number;
  pixelScale?: number;
  fade?: boolean;
  duration?: number;
  pixelDuration?: number;
  pattern?: PixelPattern;
  randomness?: number;
  easing?: string;
  trigger?: string;
}) {
  const [desiredActive, setDesiredActive] = useState(false);
  const [shownActive, setShownActive] = useState(false);
  const [transition, setTransition] = useState<{
    to: boolean;
    grid: Grid;
  } | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const layerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pixelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const animationsRef = useRef<Animation[]>([]);
  const timerRef = useRef<number>(0);
  const grid = useMemo(
    () =>
      buildGrid({
        width: box.width,
        height: box.height,
        pixelSize: Math.max(8, Math.round(pixelSize)),
        gap: Math.max(0, Math.round(gap)),
        pattern,
        randomness,
      }),
    [box.width, box.height, pixelSize, gap, pattern, randomness],
  );
  const configRef = useRef({
    duration,
    pixelDuration,
    pixelSpin,
    pixelScale,
    pixelRadius,
    fade,
    easing,
  });
  const gridRef = useRef(grid);
  configRef.current = {
    duration,
    pixelDuration,
    pixelSpin,
    pixelScale,
    pixelRadius,
    fade,
    easing,
  };
  gridRef.current = grid;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (!width || !height) return;
      setBox((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const stopAnimations = useCallback(() => {
    animationsRef.current.forEach((animation) => animation.cancel());
    animationsRef.current = [];
    pixelRefs.current.forEach((pixel) => pixel?.replaceChildren());
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = 0;
  }, []);
  useEffect(() => stopAnimations, [stopAnimations]);
  useEffect(() => {
    if (transition || desiredActive === shownActive) return;
    setTransition({ to: desiredActive, grid: gridRef.current });
  }, [desiredActive, shownActive, transition]);
  useEffect(() => {
    if (!transition) return;
    const settings = configRef.current;
    const finish = () => {
      stopAnimations();
      setShownActive(transition.to);
      setTransition(null);
    };
    const source = layerRefs.current[transition.to ? 1 : 0];
    if (
      !source ||
      !transition.grid.pixels.length ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      finish();
      return;
    }
    const total = Math.max(200, settings.duration);
    const pixelMs = clamp(settings.pixelDuration, 60, total);
    const spread = Math.max(0, total - pixelMs);
    const endScale = coverScale(
      transition.grid.size,
      transition.grid.gap,
      settings.pixelRadius,
    );
    const frames = buildKeyframes({
      ease: makeEasing(settings.easing),
      startScale: clamp(settings.pixelScale, 0.05, 1) * endScale,
      endScale,
      spin: settings.pixelSpin,
      fade: settings.fade,
    });
    transition.grid.pixels.forEach((pixel, index) => {
      const pixelElement = pixelRefs.current[index];
      if (!pixelElement) return;
      const content = document.createElement("div");
      content.className = "rb-pixel-swap__pixel-content";
      Object.assign(content.style, {
        left: `${-pixel.left}px`,
        top: `${-pixel.top}px`,
        width: `${transition.grid.width}px`,
        height: `${transition.grid.height}px`,
        transformOrigin: `${pixel.left + transition.grid.size / 2}px ${pixel.top + transition.grid.size / 2}px`,
      });
      const clone = source.cloneNode(true) as HTMLElement;
      clone.dataset.visible = "true";
      clone.removeAttribute("aria-hidden");
      content.appendChild(clone);
      pixelElement.replaceChildren(content);
      const timing: KeyframeAnimationOptions = {
        duration: pixelMs,
        delay: pixel.offset * spread,
        easing: "linear",
        fill: "both",
      };
      animationsRef.current.push(
        pixelElement.animate(frames.windowFrames, timing),
        content.animate(frames.contentFrames, timing),
      );
    });
    timerRef.current = window.setTimeout(finish, total);
    return stopAnimations;
  }, [stopAnimations, transition]);
  const interactionProps =
    trigger === "hover"
      ? {
          onMouseEnter: () => setDesiredActive(true),
          onMouseLeave: () => setDesiredActive(false),
          onFocus: () => setDesiredActive(true),
          onBlur: () => setDesiredActive(false),
          tabIndex: 0,
        }
      : {
          onClick: () => setDesiredActive((value) => !value),
          onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setDesiredActive((value) => !value);
            }
          },
          role: "button",
          tabIndex: 0,
        };
  const renderLayer = (content: ReactNode, index: number) => {
    const isShown = index === (shownActive ? 1 : 0);
    return (
      <div
        key={index}
        ref={(element) => {
          layerRefs.current[index] = element;
        }}
        className="rb-pixel-swap__layer"
        data-visible={
          isShown && !(transition && index === (transition.to ? 1 : 0))
        }
        style={{ zIndex: isShown ? 2 : 1 }}
        aria-hidden={!isShown}
      >
        {content}
      </div>
    );
  };
  return (
    <div
      ref={containerRef}
      className="rb-pixel-swap"
      style={{ aspectRatio: "16 / 10" }}
      data-active={shownActive}
      data-transitioning={Boolean(transition)}
      aria-label="Compare traditional CMS and ZhStudio CMS"
      {...interactionProps}
    >
      {renderLayer(firstContent, 0)}
      {renderLayer(secondContent, 1)}
      {transition && (
        <div className="rb-pixel-swap__grid" aria-hidden="true">
          {transition.grid.pixels.map((pixel, index) => (
            <div
              key={pixel.id}
              ref={(element) => {
                pixelRefs.current[index] = element;
              }}
              className="rb-pixel-swap__pixel"
              style={{
                left: pixel.left,
                top: pixel.top,
                width: transition.grid.size,
                height: transition.grid.size,
                borderRadius: `${clamp(pixelRadius, 0, 50)}%`,
              }}
            />
          ))}
        </div>
      )}
      <span className="rb-pixel-swap__hint">
        {trigger === "hover" ? "Hover to transform" : "Click to transform"} ↗
      </span>
    </div>
  );
}

export function ScrollExpand({
  image,
  alt,
  title,
  hint,
  startWidth,
  startHeight,
  startRadius,
  endRadius,
  mediaZoom,
  scrollDistance,
  smoothing,
  overlay,
}: {
  image: string;
  alt: string;
  title: string;
  hint: string;
  startWidth: number;
  startHeight: number;
  startRadius: number;
  endRadius: number;
  mediaZoom: number;
  scrollDistance: number;
  smoothing: number;
  overlay: number;
}) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!motionSafe()) return;
    let target = 0,
      current = 0,
      frame = 0;
    const update = () => {
      if (!root.current) return;
      const rect = root.current.getBoundingClientRect(),
        viewport = window.innerHeight;
      target = Math.max(
        0,
        Math.min(1, (viewport - rect.top) / (viewport + scrollDistance * 3)),
      );
      current += (target - current) * Math.max(0.04, smoothing);
      root.current.style.setProperty("--expand", current.toFixed(3));
      frame = requestAnimationFrame(update);
    };
    frame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frame);
  }, [scrollDistance, smoothing]);
  return (
    <div
      className="scroll-expand"
      ref={root}
      style={
        {
          "--start-w": `${startWidth}%`,
          "--start-h": `${startHeight}vh`,
          "--start-radius": `${startRadius}px`,
          "--end-radius": `${endRadius}px`,
          "--media-zoom": mediaZoom,
          "--overlay": overlay / 100,
        } as CSSProperties
      }
    >
      <div className="scroll-media">
        <img src={image} alt={alt} loading="lazy" />
        <div className="scroll-overlay" />
        <h2>{title}</h2>
        <span>{hint} ↓</span>
      </div>
    </div>
  );
}

export function AccordionGallery({
  items,
  grayscale,
  parallax,
  tilt,
  gap,
  radius,
  expandRatio,
  orientation,
  duration,
  trigger,
  selectedId,
  onSelect,
  onReorder,
}: {
  items: Item[];
  grayscale: boolean;
  parallax: number;
  tilt: number;
  gap: number;
  radius: number;
  expandRatio: number;
  orientation: string;
  duration: number;
  trigger: string;
  selectedId?: string;
  onSelect: (id: string) => void;
  onReorder: (active: string, over: string) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef<(HTMLElement | null)[]>([]);
  const mediaRefs = useRef<(HTMLElement | null)[]>([]);
  const barRefs = useRef<(HTMLElement | null)[]>([]);
  const textRefs = useRef<(HTMLElement | null)[]>([]);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  const firstRun = useRef(true);
  const mediaSize = useRef(320);
  const vertical = orientation === "vertical";
  const [active, setActive] = useState(
    Math.min(1, Math.max(items.length - 1, 0)),
  );
  const reduced = !motionSafe();
  const applyLayout = useCallback(
    (animate: boolean) => {
      if (!panelRefs.current.length) return;
      const ratio = clamp(expandRatio, 0.2, 0.9);
      const grow =
        items.length > 1 ? (ratio * (items.length - 1)) / (1 - ratio) : 1;
      timelineRef.current?.kill();
      const timeline = gsap.timeline();
      panelRefs.current.forEach((panel, index) => {
        if (!panel) return;
        const isActive = index === active;
        const rotation = isActive ? 0 : index < active ? tilt : -tilt;
        timeline.to(
          panel,
          {
            flexGrow: isActive ? grow : 1,
            ...(vertical ? { rotateX: -rotation } : { rotateY: rotation }),
            duration: animate && !reduced ? duration : 0,
            ease: "power3.out",
          },
          0,
        );
        const media = mediaRefs.current[index];
        if (media)
          timeline.to(
            media,
            {
              xPercent: -50,
              yPercent: -50,
              x: vertical
                ? 0
                : isActive
                  ? 0
                  : clamp(active - index, -1.5, 1.5) *
                    parallax *
                    mediaSize.current *
                    0.06,
              y: vertical
                ? isActive
                  ? 0
                  : clamp(active - index, -1.5, 1.5) *
                    parallax *
                    mediaSize.current *
                    0.06
                : 0,
              "--ag-gray": grayscale ? (isActive ? 0 : 1) : 0,
              "--ag-dim": isActive ? 0 : 0.35,
              duration: animate && !reduced ? duration : 0,
              ease: "power3.out",
            },
            0,
          );
        const caption = [
          barRefs.current[index],
          textRefs.current[index],
        ].filter(Boolean);
        timeline.to(
          caption,
          {
            opacity: isActive ? 1 : 0,
            x: isActive ? 0 : -14,
            duration: animate && !reduced ? duration : 0,
            stagger: reduced ? 0 : 0.06,
            ease: "power3.out",
          },
          0,
        );
      });
      timelineRef.current = timeline;
    },
    [
      active,
      duration,
      expandRatio,
      grayscale,
      items.length,
      parallax,
      reduced,
      tilt,
      vertical,
    ],
  );
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const measure = () => {
      const rect = root.getBoundingClientRect();
      const total = vertical ? rect.height : rect.width;
      const usable = Math.max(total - gap * (items.length - 1), 120);
      mediaSize.current = Math.max(
        140,
        usable * clamp(expandRatio, 0.2, 0.9) * 1.22,
      );
      root.style.setProperty("--ag-media-size", `${mediaSize.current}px`);
      applyLayout(!firstRun.current);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => observer.disconnect();
  }, [applyLayout, expandRatio, gap, items.length, vertical]);
  useEffect(() => {
    applyLayout(!firstRun.current);
    firstRun.current = false;
  }, [applyLayout]);
  useEffect(
    () => () => {
      timelineRef.current?.kill();
    },
    [],
  );
  const keyDown = (index: number, event: KeyboardEvent<HTMLElement>) => {
    if (["ArrowRight", "ArrowDown"].includes(event.key)) {
      event.preventDefault();
      setActive((index + 1) % items.length);
      panelRefs.current[(index + 1) % items.length]?.focus();
    } else if (["ArrowLeft", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      const next = (index - 1 + items.length) % items.length;
      setActive(next);
      panelRefs.current[next]?.focus();
    }
  };
  return (
    <div
      ref={rootRef}
      className={cn("rb-accordion-gallery", vertical && "is-vertical")}
      style={
        {
          "--ag-gap": `${gap}px`,
          "--ag-radius": `${radius}px`,
          height: vertical ? "760px" : "560px",
        } as CSSProperties
      }
      role="list"
      aria-label="Selected work image gallery"
    >
      {items.map((item, index) => {
        const isActive = index === active;
        return (
          <article
            key={item.id}
            ref={(element) => {
              panelRefs.current[index] = element;
            }}
            className={cn(
              "rb-ag-panel",
              isActive && "is-active",
              selectedId === item.id && "cms-selected",
            )}
            draggable
            onDragStart={(event) =>
              event.dataTransfer.setData("text/plain", item.id)
            }
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) =>
              onReorder(event.dataTransfer.getData("text/plain"), item.id)
            }
            onMouseEnter={() => trigger === "hover" && setActive(index)}
            onFocus={() => setActive(index)}
            onClick={() => {
              setActive(index);
              onSelect(item.id);
            }}
            onKeyDown={(event) => keyDown(index, event)}
            role="listitem"
            tabIndex={0}
            aria-current={isActive ? "true" : undefined}
            aria-label={`${item.title}: ${item.text ?? ""}`}
          >
            <span className="rb-ag-panel__frame">
              <span
                ref={(element) => {
                  mediaRefs.current[index] = element;
                }}
                className="rb-ag-panel__media"
              >
                <img
                  src={item.image}
                  alt={item.alt ?? item.title}
                  draggable="false"
                  loading={index < 2 ? "eager" : "lazy"}
                />
              </span>
              <span className="rb-ag-panel__overlay" aria-hidden="true" />
              <span className="rb-ag-panel__number" aria-hidden="true">
                0{index + 1}
              </span>
            </span>
            <span className="rb-ag-panel__label" aria-hidden="true">
              <span
                ref={(element) => {
                  barRefs.current[index] = element;
                }}
                className="rb-ag-panel__bar"
              />
              <span
                ref={(element) => {
                  textRefs.current[index] = element;
                }}
                className="rb-ag-panel__text"
              >
                <small>{item.text}</small>
                <strong>{item.title}</strong>
              </span>
            </span>
          </article>
        );
      })}
    </div>
  );
}
